import { FC, useEffect, useState } from "react";
import { useUserStore } from "store/user";
import { getVesselFilter, upsertVesselFolderApi } from "api/vessel";
import CustomModal from "ui/modal/Modal";
import CustomDropDownList from "ui/dropDown";
import { useTranslation } from "react-i18next";
import { Vessel } from "interface/vessel";
import { VesselRenderItem } from "ui/dropdownItemList/VesselRenderItem";
import { getUserVessels, VesselDocumentFilterOptions } from "api/vessel";
import CustomButton from "ui/Button";
import { CustomInput } from "ui/Input";
import { COLOR } from "utils/constant";
import { useInfoStore } from "store/error";
import { useNavigate } from "react-router";
import { PageTypePath } from "utils/pages";

interface CustomProps {
	setFilterOptions: (options: VesselDocumentFilterOptions) => void;
	filterOptions: VesselDocumentFilterOptions;
	// exportAsExcel: () => void;
	// openUploadModal: () => void;
	canCreate?: boolean;
	onFolderCreated?: () => void;
}
const Filter: FC<CustomProps> = ({
	setFilterOptions,
	filterOptions,
	// exportAsExcel,
	canCreate = false,
	onFolderCreated,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { isAdmin } = useUserStore();
	const [vessels, setVessels] = useState<Vessel[]>([]);

	const [showCreateFolder, setShowCreateFolder] = useState(false);
	const [folderName, setFolderName] = useState("");
	const [creating, setCreating] = useState(false);
	const [createError, setCreateError] = useState("");
	const resetStore = useInfoStore((state) => state.resetStore);
	const setInfo = useInfoStore((state) => state.setInfo);

	useEffect(() => {
		setShowCreateFolder(false);
		setFolderName("");
		setCreateError("");
	}, [filterOptions.vesselId, filterOptions.vesselRegistrationId]);

	const createFolder = async () => {
		const name = folderName.trim();
		if (
			creating ||
			!canCreate ||
			!name ||
			name.length > 50 ||
			!filterOptions.vesselId ||
			!filterOptions.vesselRegistrationId
		)
			return;
		setCreating(true);
		setCreateError("");
		try {
			await upsertVesselFolderApi({
				vesselId: filterOptions.vesselId,
				vesselRegistrationId: filterOptions.vesselRegistrationId,
				folderName: name,
			});

			setFolderName("");
			setInfo({
				title: t("Success"),
				infoType: "Success",
				show: true,
				close: () => {
					setShowCreateFolder(false);

					onFolderCreated?.();
					resetStore();
				},
				description: t("VesselFolderCreatedSuccessfully"),
				actions: [
					{
						text: t("OK"),
						disabled: false,
						btn: () => {
							setShowCreateFolder(false);
							onFolderCreated?.();
							resetStore();
						},
					},
				],
			});
			setShowCreateFolder(false);
		} catch (error: any) {
			let message = "";
			if (error?.status === 409) {
				message = t("VesselFolderAlreadyExists");
			} else if (error?.status === 403) {
				message = t("UploadAccessDeniedMessage");
			} else {
				message = t("ErrorDescription");
			}
			setCreateError(message);
		} finally {
			setCreating(false);
		}
	};

	const handleVessel = (vessel: Vessel) => {
		setFilterOptions({
			...filterOptions,
			vesselId: vessel.id,
			vesselFolderId: undefined,
			vesselName: vessel.nm,
			vesselRegistrationId: vessel.rn,
		});
		const path = `/${PageTypePath.VesselDocuments}`;
		navigate(
			vessel.id && vessel.rn
				? `${path}/${encodeURIComponent(vessel.id)}/${encodeURIComponent(vessel.rn)}`
				: path,
		);
	};

	const getVessels = async (text: string = "") => {
		try {
			if (isAdmin) {
				const data = await getVesselFilter(text);

				return data || [];
			}
			if (vessels.length > 0) {
				return vessels;
			}
			const data = await getUserVessels();
			setVessels(data || []);
			return data || [];
		} catch (error) {
			console.error(error);
			return [];
		}
	};
	return (
		<div className="filter-container">
			<div className="filter-items ">
				<div className="settlement-filter-field" style={{ width: "220px" }}>
					<CustomDropDownList
						data={vessels}
						filterable={isAdmin}
						dataItemKey="id"
						textField="nm"
						label={t("Vessel")}
						value={{
							id: filterOptions.vesselId || null,
							nm: filterOptions.vesselName || " ",
						}}
						// label="Velg Fart�y"
						defaultItem={{ id: null, nm: t("All") }}
						onSelect={handleVessel}
						serverFilter={isAdmin}
						getData={getVessels}
						itemRender={VesselRenderItem()}
						serverFilterApi={getVessels}
					/>
				</div>
			</div>

			{canCreate && (
				<div className="filter-end-btn store-statistics-filter-end-btn">
					<CustomButton
						label={`${t("New")} ${t("Folder")}`}
						disabled={
							!canCreate ||
							!filterOptions.vesselId ||
							!filterOptions.vesselRegistrationId
						}
						onClick={() => {
							setFolderName("");
							setCreateError("");
							setShowCreateFolder(true);
						}}
						// iconName={ICON_NAMES.Download}
					/>
				</div>
			)}
			{showCreateFolder && (
				<CustomModal
					title={`${t("New")} ${t("Folder")}`}
					onClose={() => {
						if (!creating) setShowCreateFolder(false);
					}}
					actions={[
						{
							label: t("Cancel"),
							disabled: creating,
							onClick: () => setShowCreateFolder(false),
						},
						{
							label: t("Create"),
							loading: creating,
							disabled:
								creating ||
								!canCreate ||
								!folderName.trim() ||
								folderName.trim().length > 50 ||
								createError !== "",
							onClick: () => createFolder(),
						},
					]}
				>
					<CustomInput
						autoFocus
						value={folderName}
						maxLength={50}
						disabled={creating}
						onChange={(value) => {
							setFolderName(value);
							setCreateError("");
						}}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault();
								void createFolder();
							}
						}}
					/>
					{createError && (
						<p role="alert" style={{ color: COLOR.StatusRed }}>
							{createError}
						</p>
					)}
				</CustomModal>
			)}
		</div>
	);
};

export default Filter;
